import type { ChangeEvent, Dispatch, SetStateAction } from 'react';
import { Fragment, useEffect, useRef } from 'react';

import DisplaySettings from '@mui/icons-material/DisplaySettings';
import ExpandMore from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  ButtonBase,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography
} from '@mui/material';

import Checkbox from '@/components/Checkbox';
import Popper from '@/components/Popper';
import {
  ADDITIONAL_ITEM,
  allColumnIdsToChangeVisibility,
  displayedMetadataSettingsColumnsList,
  displayedValueSettingsColumnsList
} from '@/pages/Catalog/constants';
import type { CatalogItem } from '@/types/catalog';

import type { Table } from '@tanstack/react-table';

const buttonBaseStyle = { color: 'primary.main', px: 0.2 };
type DisplayColumn = { displayLabel: string; accessorKey: string };
type DisplayColumnGroup = { label: string; columns: DisplayColumn[] };
type CatalogTableColumns = ReturnType<Table<CatalogItem>['getAllColumns']>;

const renderColumnOption = (
  { displayLabel, accessorKey }: DisplayColumn,
  tanStackColumns: CatalogTableColumns
) => {
  const tanStackColumn = tanStackColumns.find(({ id }) => id === accessorKey);

  return (
    <FormControlLabel
      key={accessorKey}
      control={
        <Checkbox
          checked={tanStackColumn?.getIsVisible()}
          onChange={tanStackColumn?.getToggleVisibilityHandler()}
        />
      }
      label={displayLabel}
      sx={{ m: 0, py: 0.1 }}
    />
  );
};

const renderColumnGroups = (
  columnsList: DisplayColumnGroup[],
  tanStackColumns: CatalogTableColumns
) =>
  columnsList.map(({ label, columns }) => (
    <Fragment key={label}>
      <Typography
        color='info'
        component='div'
        sx={{ fontSize: '10px', textTransform: 'uppercase' }}
      >
        {label}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', pb: 1 }}>
        {columns.map(column => renderColumnOption(column, tanStackColumns))}
      </Box>
    </Fragment>
  ));

const DisplaySettingsPopper = ({
  expandedData,
  setExpandedData,
  table,
  isForwardCurves,
  showDiscontinued,
  setShowDiscontinued
}: {
  showDiscontinued: boolean;
  setShowDiscontinued: Dispatch<SetStateAction<boolean>>;
  expandedData: boolean;
  isForwardCurves: boolean;
  setExpandedData: (newValue: boolean) => void;
  table: Table<CatalogItem>;
}) => {
  const tanStackColumns = table.getAllColumns();
  const init = useRef(false);
  const handleChangeExpandedData = (e: ChangeEvent<HTMLInputElement>) => {
    if (isForwardCurves) return;
    setExpandedData(e.target.value === '1');
    const columnIdsToChangeVisibility = new Set([
      'TimeRef',
      'PeriodType',
      'Period',
      ADDITIONAL_ITEM
    ]);
    const columns = tanStackColumns.filter(column => columnIdsToChangeVisibility.has(column.id));

    for (const column of columns) {
      column.toggleVisibility();
    }
  };

  const listedColumns = tanStackColumns.filter(column =>
    allColumnIdsToChangeVisibility.includes(column.id)
  );

  useEffect(() => {
    if (!init.current) {
      table.getColumn('Status')?.setFilterValue(false);
      init.current = true;
    }
  }, [init, table]);

  const allListedColumnsSelected = listedColumns.every(column => column.getIsVisible());

  const handleSelectClearAll = () => {
    for (const column of listedColumns) {
      column.toggleVisibility(!allListedColumnsSelected);
    }
  };

  return (
    <Popper
      buttonContent={
        <>
          <DisplaySettings sx={{ mr: 1 }} />
          Display settings
          <ExpandMore sx={{ ml: 6 }} />
        </>
      }
      buttonVariant='outlined'
      placement='bottom-end'
      zIndex={9999}
    >
      <Box
        sx={{
          minWidth: 280,
          borderBottom: '1px solid',
          borderLeft: '1px solid',
          borderColor: 'primary.main',
          display: 'flex',
          flexDirection: 'column',
          fontSize: '14px'
        }}
      >
        <Accordion
          defaultExpanded={!isForwardCurves}
          disabled={isForwardCurves}
        >
          <AccordionSummary
            expandIcon={<ExpandMore />}
            sx={{
              '&.Mui-expanded': {
                backgroundColor: 'secondary.main'
              }
            }}
          >
            Expanded Spot Catalog
          </AccordionSummary>
          <AccordionDetails sx={{ backgroundColor: 'secondary.main' }}>
            <FormControl>
              <RadioGroup
                aria-labelledby='demo-radio-buttons-group-label'
                value={expandedData ? '1' : '0'}
                onChange={handleChangeExpandedData}
              >
                <FormControlLabel
                  control={<Radio />}
                  label='Display all items for each code'
                  value='1'
                />
                <Typography
                  color='info'
                  component='div'
                  sx={{ fontSize: '12px', fontStyle: 'italic', mb: 0.5 }}
                >
                  Shows TimeRef, PeriodType, Period columns
                </Typography>
                <FormControlLabel
                  control={<Radio />}
                  label='Display one item per code'
                  value='0'
                />
              </RadioGroup>
            </FormControl>
          </AccordionDetails>
        </Accordion>
        <FormControlLabel
          control={
            <Checkbox
              checked={showDiscontinued}
              onChange={() => {
                table.getColumn('Status')?.setFilterValue(!showDiscontinued);
                setShowDiscontinued(!showDiscontinued);
              }}
            />
          }
          label='Show discontinued items'
          sx={{ m: 0, py: 1, px: 2 }}
        />
        <Accordion>
          <AccordionDetails
            sx={{
              backgroundColor: 'secondary.main',
              pb: 0
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Box>{renderColumnGroups(displayedValueSettingsColumnsList, tanStackColumns)}</Box>
            </Box>
          </AccordionDetails>
        </Accordion>
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMore />}
            sx={{
              '&.Mui-expanded': {
                backgroundColor: 'secondary.main'
              }
            }}
          >
            Additional Metadata columns
          </AccordionSummary>
          <AccordionDetails
            sx={{
              backgroundColor: 'secondary.main',
              pb: 0
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Box>{renderColumnGroups(displayedMetadataSettingsColumnsList, tanStackColumns)}</Box>
              <Box>
                <ButtonBase
                  sx={buttonBaseStyle}
                  onClick={handleSelectClearAll}
                >
                  {allListedColumnsSelected ? 'Clear all' : 'Select all'}
                </ButtonBase>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>
    </Popper>
  );
};

export default DisplaySettingsPopper;
